import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Search as SearchIcon,
  PlaylistAdd as AddAllIcon,
  InfoOutlined as InfoIcon,
  ViewColumn as ViewColumnIcon
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  filterTemplatesToExistingCategories,
  resolveOkrCategoryForTemplate,
  sortCategoriesForTemplateList,
  templateCategoryCodeForCategory,
  catalogKeyResultCodeString
} from './okrKeyResultNameTemplates';

function customKrFrequencySummary(freq) {
  if (!freq || typeof freq !== 'object') return 'All DWMQY';
  const keys = ['daily', 'weekly', 'monthly', 'quarterly', 'yearly'];
  const on = keys.filter((k) => freq[k] !== false);
  if (on.length === 5) return 'All DWMQY';
  if (!on.length) return '—';
  const short = { daily: 'D', weekly: 'W', monthly: 'M', quarterly: 'Q', yearly: 'Y' };
  return on.map((k) => short[k] || k).join('·');
}

function hexToRgb(hex) {
  if (!hex || typeof hex !== 'string') return { r: 99, g: 102, b: 241 };
  let h = hex.replace('#', '').trim();
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return { r: 99, g: 102, b: 241 };
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function categoryTintBg(hex) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r},${g},${b},0.07)`;
}

/** Aligns with KeyResultsMatrix “fill from daily” convention (informational). */
const DWMQY_WORKING_DAYS = { daily: 1, weekly: 5, monthly: 22, quarterly: 66, yearly: 250 };
const DEFAULT_HOURS_PER_WORKDAY = 8;

/**
 * Step 1: templates only for OKR categories returned by the API; Add / Remove as a single toggle control.
 */
export function KeyResultTemplatePanel({
  categories,
  isTemplateInWorkingSet,
  onAddTemplate,
  onRemoveTemplate,
  onAddAllInCategory,
  busyId,
  canAdd,
  canDelete,
  inactiveTemplateIds = [],
  onTemplateActiveChange,
  canModify = false,
  /** Return the catalog key-result row for this template when one exists (so KR Id can show the real `kr_code`). */
  resolveCatalogKeyResultForTemplate
}) {
  const theme = useTheme();
  const [q, setQ] = useState('');
  const [onlyCategoryId, setOnlyCategoryId] = useState('');
  const [sortBy, setSortBy] = useState('category_order');
  const [colMenuAnchorEl, setColMenuAnchorEl] = useState(null);
  const [visibleCols, setVisibleCols] = useState({
    krId: true,
    name: true,
    type: true,
    frequency: true,
    category: true,
    notes: true,
    active: true
  });

  const categoriesSorted = useMemo(
    () => sortCategoriesForTemplateList(null, categories || []),
    [categories]
  );

  const codeById = useMemo(() => {
    const m = new Map();
    for (const c of categories || []) {
      if (c.id) m.set(String(c.id), c);
    }
    return m;
  }, [categories]);

  const baseTemplates = useMemo(
    () => filterTemplatesToExistingCategories(categories || []),
    [categories]
  );

  const categoryCodeOrder = useMemo(() => {
    const m = new Map();
    categoriesSorted.forEach((c, i) => {
      const code = templateCategoryCodeForCategory(c);
      if (code) m.set(code, i);
    });
    return m;
  }, [categoriesSorted]);

  const rowsFiltered = useMemo(() => {
    let list = [...baseTemplates];
    if (onlyCategoryId) {
      const cat = codeById.get(String(onlyCategoryId));
      const code = templateCategoryCodeForCategory(cat);
      if (code) list = list.filter((t) => t.categoryCode === code);
    }
    const s = q.trim().toLowerCase();
    if (s) {
      list = list.filter((t) => {
        const inName = t.name.toLowerCase().includes(s);
        const inDesc = (t.description || '').toLowerCase().includes(s);
        const inId = t.id.toLowerCase().includes(s);
        const inType = (t.krType || '').toLowerCase().includes(s);
        const inFreq = (t.baseFrequency || '').toLowerCase().includes(s);
        return inName || inDesc || inId || inType || inFreq;
      });
    }
    if (sortBy === 'category_order') {
      list.sort(
        (a, b) =>
          (categoryCodeOrder.get(a.categoryCode) ?? 999) - (categoryCodeOrder.get(b.categoryCode) ?? 999) ||
          String(a.categoryCode).localeCompare(String(b.categoryCode)) ||
          String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' })
      );
    } else if (sortBy === 'category_name') {
      list.sort((a, b) => {
        const na =
          categoriesSorted.find(
            (c) =>
              templateCategoryCodeForCategory(c) === a.categoryCode
          )?.name || a.categoryCode;
        const nb =
          categoriesSorted.find(
            (c) =>
              templateCategoryCodeForCategory(c) === b.categoryCode
          )?.name || b.categoryCode;
        const c = na.localeCompare(nb, undefined, { sensitivity: 'base' });
        if (c !== 0) return c;
        return String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' });
      });
    } else if (sortBy === 'kr_name') {
      list.sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' }));
    } else if (sortBy === 'frequency') {
      const order = { Daily: 0, Weekly: 1, Monthly: 2, Quarterly: 3, Yearly: 4 };
      const f = (x) => order[x.baseFrequency || ''] ?? 9;
      list.sort((a, b) => f(a) - f(b) || String(a.name).localeCompare(String(b.name)));
    }
    return list;
  }, [q, onlyCategoryId, sortBy, categoriesSorted, codeById, baseTemplates, categoryCodeOrder]);

  const inactiveSet = useMemo(() => new Set(inactiveTemplateIds || []), [inactiveTemplateIds]);
  const templateVisibleCount =
    1 +
    (visibleCols.krId ? 1 : 0) +
    (visibleCols.name ? 1 : 0) +
    (visibleCols.type ? 1 : 0) +
    (visibleCols.frequency ? 1 : 0) +
    (visibleCols.category ? 1 : 0) +
    (visibleCols.notes ? 1 : 0) +
    (visibleCols.active ? 1 : 0) +
    1;

  const workingSetStats = useMemo(() => {
    const inFiltered = rowsFiltered.filter((t) => isTemplateInWorkingSet(t)).length;
    const inCatalog = baseTemplates.filter((t) => isTemplateInWorkingSet(t)).length;
    return {
      inFiltered,
      totalFiltered: rowsFiltered.length,
      inCatalog,
      totalCatalog: baseTemplates.length
    };
  }, [rowsFiltered, baseTemplates, isTemplateInWorkingSet]);

  const dwmqyTooltipTitle = (
    <Box sx={{ maxWidth: 360, py: 0.25 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
        DWMQY (Daily · Weekly · Monthly · Quarterly · Yearly)
      </Typography>
      <Typography variant="caption" component="div" sx={{ display: 'block', lineHeight: 1.5, mb: 1 }}>
        In step 2 (matrix), period targets can follow the same roll-up convention as <strong>Fill W/M/Q/Y from daily</strong>:
        Weekly ≈ Daily × {DWMQY_WORKING_DAYS.weekly}, Monthly ≈ ×{DWMQY_WORKING_DAYS.monthly}, Quarterly ≈ ×
        {DWMQY_WORKING_DAYS.quarterly}, Yearly ≈ ×{DWMQY_WORKING_DAYS.yearly} working days (approximate calendar-neutral
        counts).
      </Typography>
      <Typography variant="caption" component="div" sx={{ display: 'block', lineHeight: 1.5, mb: 0.75 }}>
        <strong>By period (informational)</strong>
      </Typography>
      {[
        ['Daily', `${DWMQY_WORKING_DAYS.daily} working day per day`],
        ['Weekly', `~${DWMQY_WORKING_DAYS.weekly} working days per week`],
        ['Monthly', `~${DWMQY_WORKING_DAYS.monthly} working days per month`],
        ['Quarterly', `~${DWMQY_WORKING_DAYS.quarterly} working days per quarter`],
        ['Yearly', `~${DWMQY_WORKING_DAYS.yearly} working days per year`]
      ].map(([label, detail]) => (
        <Typography key={label} variant="caption" component="div" sx={{ display: 'block', lineHeight: 1.45 }}>
          <strong>{label}:</strong> {detail}
        </Typography>
      ))}
      <Typography variant="caption" component="div" sx={{ display: 'block', lineHeight: 1.5, mt: 1 }}>
        For <strong>hours</strong>-style KRs, a common default is <strong>{DEFAULT_HOURS_PER_WORKDAY} hours</strong> per
        working day (adjust in step 2 per level).
      </Typography>
    </Box>
  );

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Box
        sx={{
          p: 1.5,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'action.hover'
        }}
      >
        <TextField
          size="small"
          placeholder="Search name, type, frequency, template id…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{ minWidth: 220, flex: '1 1 240px' }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by category</InputLabel>
          <Select value={onlyCategoryId} label="Filter by category" onChange={(e) => setOnlyCategoryId(e.target.value)}>
            <MenuItem value="">All categories</MenuItem>
            {categoriesSorted.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Sort by</InputLabel>
          <Select value={sortBy} label="Sort by" onChange={(e) => setSortBy(e.target.value)}>
            <MenuItem value="category_order">Category (as in OKR Categories)</MenuItem>
            <MenuItem value="category_name">Category name (A–Z)</MenuItem>
            <MenuItem value="kr_name">Key result name (A–Z)</MenuItem>
            <MenuItem value="frequency">Base frequency (Daily → Yearly)</MenuItem>
          </Select>
        </FormControl>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddAllIcon />}
          disabled={!canAdd || Boolean(busyId) || !onlyCategoryId}
          onClick={() => {
            const cat = codeById.get(String(onlyCategoryId));
            const code = templateCategoryCodeForCategory(cat);
            if (code) onAddAllInCategory(code);
          }}
          type="button"
        >
          {(() => {
            const ac = onlyCategoryId ? codeById.get(String(onlyCategoryId))?.category_code : null;
            const adding = ac && busyId === `all-${ac}`;
            return adding ? 'Adding…' : 'Add all missing (this category filter)';
          })()}
        </Button>
        <Tooltip title={dwmqyTooltipTitle} placement="left" arrow>
          <IconButton size="small" aria-label="About DWMQY periods and working days" sx={{ ml: 'auto' }}>
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ViewColumnIcon fontSize="small" />}
          onClick={(e) => setColMenuAnchorEl(e.currentTarget)}
          type="button"
        >
          Columns
        </Button>
        <Menu
          anchorEl={colMenuAnchorEl}
          open={Boolean(colMenuAnchorEl)}
          onClose={() => setColMenuAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {[
            ['krId', 'KR Id'],
            ['name', 'Key result name'],
            ['type', 'Type'],
            ['frequency', 'Base frequency'],
            ['category', 'Category'],
            ['notes', 'Notes'],
            ['active', 'Active']
          ].map(([key, label]) => (
            <MenuItem
              key={key}
              onClick={() => setVisibleCols((prev) => ({ ...prev, [key]: !prev[key] }))}
              dense
            >
              <Checkbox size="small" checked={Boolean(visibleCols[key])} />
              <Typography variant="body2">{label}</Typography>
            </MenuItem>
          ))}
        </Menu>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.5, py: 1, lineHeight: 1.45 }}>
        Only categories from <strong>Administration → OKR Categories</strong> appear here (colors match their theme). Use
        one button per row: <strong>Add</strong> when the key result is not in this job role&apos;s working set; after it
        joins the set, the same control becomes <strong>Remove</strong>.         Each catalog line gets a <strong>unique
        sequential</strong> id (<strong>CATEGORY–SYST–</strong> plus a 12-digit server sequence) the first time it is
        created. Until then, the id column shows the <strong>template library</strong> reference; after you add a line, it
        shows the <strong>catalog</strong> id, same pattern as custom lines: <strong>category code–SYST–</strong> (12-digit
        server sequence, same as <strong>–CUST–</strong>). The category code is always from <strong>Administration → OKR
        Categories</strong>, not the name-template library id.{' '}
        <strong>Add all missing</strong> needs one category selected in the filter. Click (i) for DWMQY roll-up hints.
      </Typography>

      <TableContainer sx={{ maxHeight: 'min(62vh, 560px)' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell width={48} align="right">
                S.No.
              </TableCell>
              {visibleCols.krId && (
                <TableCell width={200}>
                  <Tooltip
                    title="Catalog KR Id: your OKR category’s 4-character code, then SYST, then the same 12-digit sequence format as custom (CUST) lines. Assigned when the line is first saved. Before that, a category-based placeholder is shown (not the internal library id)."
                    placement="top"
                    arrow
                  >
                    <span>KR Id</span>
                  </Tooltip>
                </TableCell>
              )}
              {visibleCols.name && <TableCell>Key result name</TableCell>}
              {visibleCols.type && <TableCell width={92}>Type</TableCell>}
              {visibleCols.frequency && <TableCell width={108}>Base frequency</TableCell>}
              {visibleCols.category && <TableCell width={200}>Category</TableCell>}
              {visibleCols.notes && <TableCell>Notes</TableCell>}
              {visibleCols.active && (
                <TableCell width={100} align="center">
                  Active
                </TableCell>
              )}
              <TableCell width={168} align="center">
                Add/Remove to Working Set
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rowsFiltered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={templateVisibleCount}>
                  <Typography variant="body2" color="text.secondary">
                    {baseTemplates.length === 0
                      ? 'No templates match your OKR categories (ensure each category has a category code like CNDP, CLBD, …).'
                      : 'No templates match the current filters.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rowsFiltered.map((t, idx) => {
                const cat = resolveOkrCategoryForTemplate(categories, t);
                const hex = cat?.theme_color || theme.palette.primary.main;
                const inSet = isTemplateInWorkingSet(t);
                const busy = busyId === t.id;
                const addAllThis = busyId === `all-${t.categoryCode}`;
                const otherBusy = Boolean(busyId) && !busy && !addAllThis;
                const rowBg = cat ? categoryTintBg(hex) : theme.palette.action.hover;
                const borderC = cat ? alpha(hex, 0.22) : 'divider';
                // KR Id prefix must follow Key Result *category* (Admin → OKR Categories), not the built-in template row id (e.g. SOBR-LIB-022)
                const fromCategory =
                  (cat && String(cat.category_code || '').trim()) || (cat ? templateCategoryCodeForCategory(cat) : '') || '';
                const catCode = fromCategory || t.categoryCode || '—';
                const catCodeUpper = String(fromCategory || catCode || '')
                  .toUpperCase()
                  .replace(/[^A-Z0-9]/g, '');
                const hasValidCode = catCodeUpper.length === 4;
                const catalogRow = resolveCatalogKeyResultForTemplate
                  ? resolveCatalogKeyResultForTemplate(t)
                  : null;
                const assignedKrId = catalogKeyResultCodeString(catalogRow);
                const libraryActive = !inactiveSet.has(t.id);

                return (
                  <TableRow
                    key={t.id}
                    hover
                    sx={{
                      bgcolor: rowBg,
                      '& td': { borderColor: borderC, verticalAlign: 'middle' }
                    }}
                  >
                    <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      {idx + 1}
                    </TableCell>
                    {visibleCols.krId && (
                      <TableCell>
                        {assignedKrId ? (
                          <Typography
                            variant="caption"
                            title={assignedKrId}
                            sx={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}
                          >
                            {assignedKrId}
                          </Typography>
                        ) : (
                          <Tooltip
                            title={
                              hasValidCode
                                ? `No catalog line yet, so there is no stored KR Id. After you click Add, the server creates ${catCodeUpper}-SYST- + the same 12-digit sequence style as custom (CUST) lines. (Template library id: ${t.id} — not the catalog KR Id.)`
                                : 'Set a 4-character Key Result category code in Administration, then add this key result. The full catalog id is only stored after the first save.'
                            }
                            placement="top"
                            arrow
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontStyle: 'italic', cursor: 'default' }}
                            >
                              Not in catalog
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                    {visibleCols.name && (
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          color: cat ? hex : 'text.primary',
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                          maxWidth: 360
                        }}
                      >
                        {t.name}
                      </TableCell>
                    )}
                    {visibleCols.type && (
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          System
                        </Typography>
                        {t.krType ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                            {t.krType}
                          </Typography>
                        ) : null}
                      </TableCell>
                    )}
                    {visibleCols.frequency && <TableCell>{t.baseFrequency || '—'}</TableCell>}
                    {visibleCols.category && (
                      <TableCell>
                        {cat ? (
                          <Box>
                            <Chip
                              size="small"
                              label={cat.name}
                              sx={{
                                fontWeight: 600,
                                bgcolor: alpha(hex, 0.18),
                                border: `1px solid ${alpha(hex, 0.35)}`
                              }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                              {String(catCode).toUpperCase()}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    {visibleCols.notes && (
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                          {t.description}
                        </Typography>
                      </TableCell>
                    )}
                    {visibleCols.active && (
                      <TableCell align="center">
                        <Switch
                          size="small"
                          checked={libraryActive}
                          disabled={!canModify || !onTemplateActiveChange}
                          onChange={(e) => onTemplateActiveChange?.(t.id, e.target.checked)}
                          inputProps={{ 'aria-label': `Template ${t.id} active in library` }}
                        />
                      </TableCell>
                    )}
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.35 }}>
                        <Button
                          size="small"
                          variant={inSet ? 'outlined' : 'contained'}
                          color={inSet ? 'error' : 'primary'}
                          disabled={
                            otherBusy ||
                            busy ||
                            addAllThis ||
                            (!inSet && (!canAdd || !libraryActive)) ||
                            (inSet && !canDelete)
                          }
                          onClick={() => {
                            if (busy || addAllThis || otherBusy) return;
                            if (inSet) void onRemoveTemplate(t);
                            else void onAddTemplate(t);
                          }}
                          sx={{ minWidth: 104, textTransform: 'none', fontWeight: 700 }}
                        >
                          {busy ? <CircularProgress size={18} color="inherit" /> : inSet ? 'Remove' : 'Add'}
                        </Button>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                          {!libraryActive
                            ? 'Inactive (library)'
                            : inSet
                              ? 'Click to Remove'
                              : 'Click to Add'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.25,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'action.hover'
        }}
      >
        <Typography variant="body2" color="text.secondary">
          <strong>This list:</strong> {workingSetStats.inFiltered} added out of {workingSetStats.totalFiltered}
        </Typography>
        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
        <Typography variant="body2" color="text.secondary">
          <strong>Full template catalog:</strong> {workingSetStats.inCatalog} added out of {workingSetStats.totalCatalog}
        </Typography>
      </Box>
    </Paper>
  );
}

/**
 * Custom catalog Key Results (CUST lines only): same working-set controls as templates; separate filters from step 1 template list.
 */
export function KeyResultCustomCatalogPanel({
  categories,
  rows,
  isInWorkingSet,
  onAdd,
  onRemove,
  onToggleActive,
  onCreateCustom,
  busyId,
  canAdd,
  canDelete,
  canModify
}) {
  const theme = useTheme();
  const [q, setQ] = useState('');
  const [onlyCategoryId, setOnlyCategoryId] = useState('');
  const [sortBy, setSortBy] = useState('category_order');
  const [colMenuAnchorEl, setColMenuAnchorEl] = useState(null);
  const [visibleCols, setVisibleCols] = useState({
    krId: true,
    name: true,
    type: true,
    dwmqy: true,
    category: true,
    notes: true,
    active: true
  });

  const categoriesSorted = useMemo(
    () => sortCategoriesForTemplateList(null, categories || []),
    [categories]
  );

  const codeById = useMemo(() => {
    const m = new Map();
    for (const c of categories || []) {
      if (c.id) m.set(String(c.id), c);
    }
    return m;
  }, [categories]);

  const categoryCodeOrder = useMemo(() => {
    const m = new Map();
    categoriesSorted.forEach((c, i) => {
      const code = templateCategoryCodeForCategory(c);
      if (code) m.set(code, i);
    });
    return m;
  }, [categoriesSorted]);

  const rowsFiltered = useMemo(() => {
    let list = [...(rows || [])];
    if (onlyCategoryId) {
      list = list.filter((kr) => String(kr.category_id) === String(onlyCategoryId));
    }
    const s = q.trim().toLowerCase();
    if (s) {
      list = list.filter((kr) => {
        const nm = String(kr.name || '').toLowerCase();
        const desc = String(kr.description || '').toLowerCase();
        const idc = String(kr.kr_code || '').toLowerCase();
        const dw = customKrFrequencySummary(kr.key_result_frequency).toLowerCase();
        return nm.includes(s) || desc.includes(s) || idc.includes(s) || dw.includes(s);
      });
    }
    if (sortBy === 'category_order') {
      list.sort((a, b) => {
        const ca = codeById.get(String(a.category_id));
        const cb = codeById.get(String(b.category_id));
        const ac = templateCategoryCodeForCategory(ca) || '';
        const bc = templateCategoryCodeForCategory(cb) || '';
        const ord = (categoryCodeOrder.get(ac) ?? 999) - (categoryCodeOrder.get(bc) ?? 999);
        if (ord !== 0) return ord;
        const c = String(ac).localeCompare(String(bc));
        if (c !== 0) return c;
        return String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
      });
    } else if (sortBy === 'category_name') {
      list.sort((a, b) => {
        const na = codeById.get(String(a.category_id))?.name || '';
        const nb = codeById.get(String(b.category_id))?.name || '';
        const c = na.localeCompare(nb, undefined, { sensitivity: 'base' });
        if (c !== 0) return c;
        return String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
      });
    } else if (sortBy === 'kr_name') {
      list.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }));
    } else if (sortBy === 'kr_code') {
      list.sort((a, b) =>
        String(a.kr_code || '').localeCompare(String(b.kr_code || ''), undefined, { numeric: true, sensitivity: 'base' })
      );
    }
    return list;
  }, [rows, q, onlyCategoryId, sortBy, categoriesSorted, codeById, categoryCodeOrder]);

  const workingSetStats = useMemo(() => {
    const base = rows || [];
    const inFiltered = rowsFiltered.filter((kr) => isInWorkingSet(kr)).length;
    const inAll = base.filter((kr) => isInWorkingSet(kr)).length;
    return {
      inFiltered,
      totalFiltered: rowsFiltered.length,
      inAll,
      totalAll: base.length
    };
  }, [rowsFiltered, rows, isInWorkingSet]);
  const customVisibleCount =
    1 +
    (visibleCols.krId ? 1 : 0) +
    (visibleCols.name ? 1 : 0) +
    (visibleCols.type ? 1 : 0) +
    (visibleCols.dwmqy ? 1 : 0) +
    (visibleCols.category ? 1 : 0) +
    (visibleCols.notes ? 1 : 0) +
    (visibleCols.active ? 1 : 0) +
    1;

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mt: 2.5 }}>
      <Box sx={{ px: 1.5, py: 1.25, borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Custom Key Results
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              Only <strong>custom</strong> catalog lines (type Custom; KR Id <strong>…-CUST-…</strong>). Same working-set
              rules as templates. Inactive rows cannot be added.
            </Typography>
          </Box>
          <Tooltip
            title={
              <Typography variant="caption">
                DWMQY = Daily, Weekly, Monthly, Quarterly, Yearly target periods. Values can be derived from daily in the
                matrix editor.
              </Typography>
            }
            placement="left"
            arrow
          >
            <IconButton size="small" aria-label="About DWMQY periods">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {onCreateCustom ? (
            <Button size="small" variant="contained" onClick={() => onCreateCustom()} disabled={!canAdd} type="button">
              Create custom Key Result
            </Button>
          ) : null}
        </Box>
      </Box>

      <Box
        sx={{
          p: 1.5,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'action.hover'
        }}
      >
        <TextField
          size="small"
          placeholder="Search name, KR Id, DWMQY, notes…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{ minWidth: 220, flex: '1 1 240px' }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by category</InputLabel>
          <Select value={onlyCategoryId} label="Filter by category" onChange={(e) => setOnlyCategoryId(e.target.value)}>
            <MenuItem value="">All categories</MenuItem>
            {categoriesSorted.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Sort by</InputLabel>
          <Select value={sortBy} label="Sort by" onChange={(e) => setSortBy(e.target.value)}>
            <MenuItem value="category_order">Category (as in OKR Categories)</MenuItem>
            <MenuItem value="category_name">Category name (A–Z)</MenuItem>
            <MenuItem value="kr_name">Key result name (A–Z)</MenuItem>
            <MenuItem value="kr_code">KR Id (A–Z / numeric)</MenuItem>
          </Select>
        </FormControl>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ViewColumnIcon fontSize="small" />}
          onClick={(e) => setColMenuAnchorEl(e.currentTarget)}
          type="button"
        >
          Columns
        </Button>
        <Menu
          anchorEl={colMenuAnchorEl}
          open={Boolean(colMenuAnchorEl)}
          onClose={() => setColMenuAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {[
            ['krId', 'KR Id'],
            ['name', 'Name'],
            ['type', 'Type'],
            ['dwmqy', 'DWMQY'],
            ['category', 'Category'],
            ['notes', 'Notes'],
            ['active', 'Active']
          ].map(([key, label]) => (
            <MenuItem
              key={key}
              onClick={() => setVisibleCols((prev) => ({ ...prev, [key]: !prev[key] }))}
              dense
            >
              <Checkbox size="small" checked={Boolean(visibleCols[key])} />
              <Typography variant="body2">{label}</Typography>
            </MenuItem>
          ))}
        </Menu>
      </Box>

      <TableContainer sx={{ maxHeight: 'min(48vh, 420px)' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell width={48} align="right">
                S.No.
              </TableCell>
              {visibleCols.krId && <TableCell width={132}>KR Id</TableCell>}
              {visibleCols.name && <TableCell>Name</TableCell>}
              {visibleCols.type && <TableCell width={88}>Type</TableCell>}
              {visibleCols.dwmqy && <TableCell width={100}>DWMQY</TableCell>}
              {visibleCols.category && <TableCell width={200}>Category</TableCell>}
              {visibleCols.notes && <TableCell>Notes</TableCell>}
              {visibleCols.active && (
                <TableCell width={100} align="center">
                  Active
                </TableCell>
              )}
              <TableCell width={168} align="center">
                Add/Remove to Working Set
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!rows?.length ? (
              <TableRow>
                <TableCell colSpan={customVisibleCount}>
                  <Typography variant="body2" color="text.secondary">
                    No custom Key Results yet. Use <strong>Create custom Key Result</strong> above, or step 2 (matrix){' '}
                    <strong>+Add</strong> / Create Key Result for this job role.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : rowsFiltered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={customVisibleCount}>
                  <Typography variant="body2" color="text.secondary">
                    No custom rows match the current filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rowsFiltered.map((kr, idx) => {
                const cat =
                  kr.category ||
                  (categories || []).find((c) => c && String(c.id) === String(kr.category_id)) ||
                  null;
                const hex = cat?.theme_color || theme.palette.primary.main;
                const rowBg = cat ? categoryTintBg(hex) : theme.palette.action.hover;
                const borderC = cat ? alpha(hex, 0.22) : 'divider';
                const catCode = (cat && (cat.category_code || '').trim()) || '—';
                const inSet = isInWorkingSet(kr);
                const busy = busyId === `custom-${kr.id}` || busyId === `active-${kr.id}`;
                const otherBusy = Boolean(busyId) && !busy;
                const rowActive = kr.is_active !== false;

                return (
                  <TableRow
                    key={kr.id}
                    hover
                    sx={{
                      bgcolor: rowBg,
                      '& td': { borderColor: borderC, verticalAlign: 'middle' }
                    }}
                  >
                    <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      {idx + 1}
                    </TableCell>
                    {visibleCols.krId && (
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>
                          {kr.kr_code || '—'}
                        </Typography>
                      </TableCell>
                    )}
                    {visibleCols.name && (
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          color: cat ? hex : 'text.primary',
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                          maxWidth: 360
                        }}
                      >
                        {kr.name}
                      </TableCell>
                    )}
                    {visibleCols.type && <TableCell>Custom</TableCell>}
                    {visibleCols.dwmqy && <TableCell>{customKrFrequencySummary(kr.key_result_frequency)}</TableCell>}
                    {visibleCols.category && (
                      <TableCell>
                        {cat ? (
                          <Box>
                            <Chip
                              size="small"
                              label={cat.name}
                              sx={{
                                fontWeight: 600,
                                bgcolor: alpha(hex, 0.18),
                                border: `1px solid ${alpha(hex, 0.35)}`
                              }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                              {String(catCode).toUpperCase()}
                            </Typography>
                          </Box>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    )}
                    {visibleCols.notes && (
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                          {kr.description || '—'}
                        </Typography>
                      </TableCell>
                    )}
                    {visibleCols.active && (
                      <TableCell align="center">
                        <Switch
                          size="small"
                          checked={rowActive}
                          disabled={!canModify}
                          onChange={(e) => onToggleActive?.(kr, e.target.checked)}
                          inputProps={{ 'aria-label': `Catalog ${kr.name} active` }}
                        />
                      </TableCell>
                    )}
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.35 }}>
                        <Button
                          size="small"
                          variant={inSet ? 'outlined' : 'contained'}
                          color={inSet ? 'error' : 'primary'}
                          disabled={
                            otherBusy ||
                            busy ||
                            (!inSet && (!canAdd || !rowActive)) ||
                            (inSet && !canDelete)
                          }
                          onClick={() => {
                            if (busy || otherBusy) return;
                            if (inSet) void onRemove(kr);
                            else void onAdd(kr);
                          }}
                          sx={{ minWidth: 104, textTransform: 'none', fontWeight: 700 }}
                        >
                          {busy ? <CircularProgress size={18} color="inherit" /> : inSet ? 'Remove' : 'Add'}
                        </Button>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                          {!rowActive ? 'Inactive' : inSet ? 'Click to Remove' : 'Click to Add'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.25,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'action.hover'
        }}
      >
        <Typography variant="body2" color="text.secondary">
          <strong>This list:</strong> {workingSetStats.inFiltered} added out of {workingSetStats.totalFiltered}
        </Typography>
        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
        <Typography variant="body2" color="text.secondary">
          <strong>Full custom catalog:</strong> {workingSetStats.inAll} added out of {workingSetStats.totalAll}
        </Typography>
      </Box>
    </Paper>
  );
}
